import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, UpdateOrderRequestBody, UpdateVisibilityRequestBody } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { HrailTower, InsertHrailTowerBody, UpdateHrailTowerBody } from "./types/towers";

export const useHrailTowersApi = () => {
  const api = useApi();

  const getAdminHrailTower = useCallback(
    (towerId: string, signal: AbortSignal) => {
      const url = `/admin/settings/hrail/towers/${towerId}`;
      return useSWRMutation<HrailTower, CustomError, string>(url, (url) => api.get<HrailTower>({ url, signal }));
    },
    [api],
  );

  const useAdminHrailTowers = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/towers`, query);
      return useSWRImmutable<Paginated<HrailTower>, CustomError, string>(url, (url) =>
        api.get<Paginated<HrailTower>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminHrailTowers = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/hrail/towers`, query);
      return api.get<Paginated<HrailTower>>({ url, signal }).then((x) => x.items);
    },
    [api],
  );

  const insertAdminHrailTower = useCallback(
    async (body: InsertHrailTowerBody): Promise<{ id: string }> => {
      const id = await api.post<{ id: string }, InsertHrailTowerBody>(`/admin/settings/hrail/towers`, body);
      mutateCache([`/admin/settings/hrail/towers`]);
      return id;
    },
    [api],
  );

  const updateAdminHrailTower = useCallback(
    async (towerId: string, body: UpdateHrailTowerBody): Promise<void> => {
      const url = `/admin/settings/hrail/towers/${towerId}`;
      await api.put<UpdateHrailTowerBody>(url, body);
      mutateCache([`/admin/settings/hrail/towers`], url);
    },
    [api],
  );

  const updateAdminHrailTowerVisibility = useCallback(
    async (towerId: string, visibility: UpdateVisibilityRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/towers/${towerId}/visibility`;
      await api.put<UpdateVisibilityRequestBody>(url, visibility);
      mutateCache([`/admin/settings/hrail/towers`]);
    },
    [api],
  );

  const updateAdminHrailTowerOrder = useCallback(
    async (towerId: string, order: UpdateOrderRequestBody): Promise<void> => {
      const url = `/admin/settings/hrail/towers/${towerId}/order`;
      await api.put<UpdateOrderRequestBody>(url, order);
      mutateCache([`/admin/settings/hrail/towers`]);
    },
    [api],
  );

  const deleteAdminHrailTower = useCallback(
    async (towerId: string): Promise<void> => {
      await api.del(`/admin/settings/hrail/towers/${towerId}`);
      mutateCache([`/admin/settings/hrail/towers`]);
    },
    [api],
  );

  return {
    getAdminHrailTower,
    useAdminHrailTowers,
    getAdminHrailTowers,
    insertAdminHrailTower,
    updateAdminHrailTower,
    updateAdminHrailTowerVisibility,
    updateAdminHrailTowerOrder,
    deleteAdminHrailTower,
  };
};
